<template>
    <div
        class="toolbar-section flex-wrap gap-y-4 !items-end !justify-start"
        style="flex: 1 1 auto"
        v-if="animation.active"
    >
        <div class="toolbar-wrapper">
            <div class="toolbar-group">
                <button
                    type="button"
                    title="Previous frame"
                    :disabled="animation.currentFrame <= 1 || animation.playing"
                    @click="emit('action', 'animationPrevious')"
                >
                    <ion-icon name="chevron-back-outline"></ion-icon>
                </button>
                <span class="px-3 flex items-center whitespace-nowrap">
                    Frame {{ animation.currentFrame }} / {{ frameCount }}
                </span>
                <button
                    type="button"
                    title="Next frame"
                    :disabled="
                        animation.currentFrame >= frameCount ||
                        animation.playing
                    "
                    @click="emit('action', 'animationNext')"
                >
                    <ion-icon name="chevron-forward-outline"></ion-icon>
                </button>
                <button
                    type="button"
                    title="Add frame"
                    :disabled="animation.playing"
                    @click="emit('action', 'animationAddFrame')"
                >
                    <ion-icon name="add-outline"></ion-icon>
                </button>
                <button
                    type="button"
                    title="Delete frame"
                    :disabled="frameCount <= 1 || animation.playing"
                    @click="emit('action', 'animationDeleteFrame')"
                >
                    <ion-icon name="trash-outline"></ion-icon>
                </button>
                <button
                    type="button"
                    :title="
                        animation.playing ? 'Stop animation' : 'Play animation'
                    "
                    :disabled="frameCount < 2 && !animation.playing"
                    @click="emit('action', 'animationTogglePlayback')"
                >
                    <ion-icon
                        :name="
                            animation.playing ? 'stop-outline' : 'play-outline'
                        "
                    ></ion-icon>
                </button>
                <button
                    type="button"
                    :title="animation.showLines ? 'Hide lines' : 'Show lines'"
                    :class="{ active: animation.showLines }"
                    @click="emit('action', 'animationToggleLines')"
                >
                    <ion-icon
                        :name="
                            animation.showLines
                                ? 'eye-outline'
                                : 'eye-off-outline'
                        "
                    ></ion-icon>
                </button>
                <button
                    type="button"
                    :title="
                        animation.hasBall
                            ? 'Select ball; drag it onto a player to attach'
                            : 'Add ball'
                    "
                    :disabled="animation.playing"
                    :class="{
                        active: animation.selectedPlayerId === 'animation-ball',
                    }"
                    @click="emit('action', 'animationAddBall')"
                >
                    <ion-icon name="football-outline"></ion-icon>
                </button>
                <button
                    type="button"
                    title="Close animation"
                    @click="emit('action', 'animationClose')"
                    class="text-[20px] flex items-center"
                >
                    <ion-icon name="close-outline" width="32"></ion-icon>
                </button>
            </div>
        </div>
        <div class="toolbar-wrapper">
            <div class="toolbar-group">
                <button
                    type="button"
                    title="Toggle 3D camera"
                    :class="{ active: animation.camera3d }"
                    @click="emit('action', 'animationToggle3D')"
                >
                    <ion-icon name="cube-outline"></ion-icon>
                </button>
                <template v-if="animation.camera3d">
                    <button
                        v-for="preset in cameraPresets"
                        :key="preset.value"
                        type="button"
                        :title="`${preset.label} camera view`"
                        :class="{
                            active: animation.cameraPreset === preset.value,
                        }"
                        @click="
                            emit(
                                'action',
                                'animationCameraPreset',
                                preset.value
                            )
                        "
                    >
                        {{ preset.label }}
                    </button>
                </template>
            </div>
        </div>
        <div class="toolbar-wrapper">
            <div class="toolbar-group">
                <span class="px-3 flex items-center whitespace-nowrap">
                    {{ animation.selectedPlayerLabel || "Select player" }}
                </span>
                <button
                    v-for="speed in playerSpeeds"
                    :key="speed"
                    type="button"
                    :title="`Set selected player speed to ${speed}x`"
                    :disabled="!animation.selectedPlayerId || animation.playing"
                    :class="{
                        active:
                            animation.selectedPlayerId &&
                            animation.selectedPlayerSpeed === speed,
                    }"
                    @click="emit('action', 'animationSetPlayerSpeed', speed)"
                >
                    {{ speed }}x
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import useWhiteboard from "../useWhiteboard";
import { computed } from "vue";

const { animation } = useWhiteboard();
const emit = defineEmits(["action"]);
const playerSpeeds = [0.5, 1, 1.5, 2];
const cameraPresets = [
    { label: "Top", value: "top" },
    { label: "Bird", value: "bird" },
    { label: "Stadium", value: "stadium" },
];
const frameCount = computed(() =>
    Math.max(1, Object.keys(animation.value.frames || {}).length)
);
</script>

<style scoped>
button:disabled {
    cursor: not-allowed;
    opacity: 0.35;
}
</style>
